import { useEffect, useState } from 'react'
import type { MenubarInputs } from '../types/menubar-inputs'
import type { MenubarViewToggles } from '../types/menubar-view-toggles'

type UseViewMenuTogglesArgs = Pick<
  MenubarInputs,
  | 'snapToGrid'
  | 'showGuides'
  | 'showSmartGuides'
  | 'snapToGuides'
  | 'showBleedBar'
  | 'showTextLinking'
  | 'showStickyNotes'
  | 'onToggleFullPica'
  | 'onToggleHalfPica'
  | 'onToggleSnapToGrid'
  | 'onToggleShowGuides'
  | 'onToggleShowSmartGuides'
  | 'onToggleSnapToGuides'
  | 'onToggleShowBleedBar'
  | 'onToggleShowTextLinking'
  | 'onToggleShowStickyNotes'
> & { gridMode: 'off' | 'full' | 'half' }

/**
 * View-menu checkbox state. Each toggle is mirrored locally (default on) and re-synced whenever
 * the parent passes a boolean for it; every change updates the mirror, then notifies the parent.
 */
export function useViewMenuToggles({
  gridMode,
  snapToGrid: snapToGridProp,
  showGuides: showGuidesProp,
  showSmartGuides: showSmartGuidesProp,
  snapToGuides: snapToGuidesProp,
  showBleedBar: showBleedBarProp,
  showTextLinking: showTextLinkingProp,
  showStickyNotes: showStickyNotesProp,
  onToggleFullPica,
  onToggleHalfPica,
  onToggleSnapToGrid,
  onToggleShowGuides,
  onToggleShowSmartGuides,
  onToggleSnapToGuides,
  onToggleShowBleedBar,
  onToggleShowTextLinking,
  onToggleShowStickyNotes,
}: UseViewMenuTogglesArgs): MenubarViewToggles {
  const [snapToGrid, setSnapToGrid] = useState(true)
  const [showGuides, setShowGuides] = useState(true)
  const [showSmartGuides, setShowSmartGuides] = useState(true)
  const [snapToGuides, setSnapToGuides] = useState(true)
  const [showBleedBar, setShowBleedBar] = useState(true)
  const [showTextLinking, setShowTextLinking] = useState(true)
  const [showStickyNotes, setShowStickyNotes] = useState(true)

  useEffect(() => {
    const syncIfBoolean = (value: boolean | undefined, set: (next: boolean) => void) => {
      if (typeof value === 'boolean') {
        set(value)
      }
    }

    syncIfBoolean(snapToGridProp, setSnapToGrid)
    syncIfBoolean(showGuidesProp, setShowGuides)
    syncIfBoolean(showSmartGuidesProp, setShowSmartGuides)
    syncIfBoolean(snapToGuidesProp, setSnapToGuides)
    syncIfBoolean(showBleedBarProp, setShowBleedBar)
    syncIfBoolean(showTextLinkingProp, setShowTextLinking)
    syncIfBoolean(showStickyNotesProp, setShowStickyNotes)
  }, [
    snapToGridProp,
    showGuidesProp,
    showSmartGuidesProp,
    snapToGuidesProp,
    showBleedBarProp,
    showTextLinkingProp,
    showStickyNotesProp,
  ])

  const toggle =
    (set: (next: boolean) => void, notify?: (checked: boolean) => void) => (next: boolean) => {
      set(next)
      notify?.(next)
    }

  const clearGrid = () => {
    setSnapToGrid(false)
    onToggleSnapToGrid?.(false)
    if (gridMode === 'full') {
      onToggleFullPica?.(false)
    } else if (gridMode === 'half') {
      onToggleHalfPica?.(false)
    }
  }

  const checkGridMode = (mode: 'full' | 'half', checked: boolean) => {
    const onToggleMode = mode === 'full' ? onToggleFullPica : onToggleHalfPica

    onToggleMode?.(Boolean(checked))
    setSnapToGrid(true)
    onToggleSnapToGrid?.(true)
    if (gridMode === mode) {
      setSnapToGrid(false)
      onToggleSnapToGrid?.(false)
      onToggleMode?.(Boolean(checked))
    }
  }

  return {
    snapToGrid,
    showGuides,
    showSmartGuides,
    snapToGuides,
    showBleedBar,
    showTextLinking,
    showStickyNotes,
    setSnapToGrid: toggle(setSnapToGrid, onToggleSnapToGrid),
    setShowGuides: toggle(setShowGuides, onToggleShowGuides),
    setShowSmartGuides: toggle(setShowSmartGuides, onToggleShowSmartGuides),
    setSnapToGuides: toggle(setSnapToGuides, onToggleSnapToGuides),
    setShowBleedBar: toggle(setShowBleedBar, onToggleShowBleedBar),
    setShowTextLinking: toggle(setShowTextLinking, onToggleShowTextLinking),
    setShowStickyNotes: toggle(setShowStickyNotes, onToggleShowStickyNotes),
    clearGrid,
    checkGridMode,
  }
}
